import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'

export function useCollectableFees(filters: { traderId?: number }, enabled = true) {
  const { data, isLoading } = trpc.collection.listCollectableFees.useQuery({ filters }, { enabled })
  return { data, isLoading }
}

export function useUnpaidLateFeesByOffer(traderId: number | undefined, offerIds?: number[], enabled = true) {
  const isEnabled = enabled && traderId != null && traderId > 0
  const { data, isLoading } = trpc.collection.unpaidLateFeesByOffer.useQuery(
    offerIds != null ? { traderId: traderId ?? 0, offerIds } : { traderId: traderId ?? 0 },
    { enabled: isEnabled }
  )
  return { rows: data?.rows ?? [], isLoading: isEnabled && isLoading }
}

export function useCollectedFees(filters: { traderId?: number }, enabled = true) {
  const { data, isLoading } = trpc.collection.listCollectedFees.useQuery({ filters }, { enabled })
  return { data, isLoading }
}

export function useCollectableAdvanceRecoveries(
  filters: { traderId?: number },
  options: { includeNotDue?: boolean; enabled?: boolean } = {}
) {
  const { data, isLoading } = trpc.collection.listCollectableAdvanceRecoveries.useQuery(
    { filters, includeNotDue: options.includeNotDue ?? false },
    { enabled: options.enabled ?? true }
  )
  return { data, isLoading }
}

const COLLECTIONS_POLL_INTERVAL_MS = 8000

export function useAdvanceRecoveryDueChart(traderId?: number, enabled = true) {
  const { data, isLoading } = trpc.collection.advanceRecoveryDueChart.useQuery(
    { traderId },
    { enabled, refetchInterval: COLLECTIONS_POLL_INTERVAL_MS }
  )
  return { buckets: data ?? [], isLoading }
}

export function useCollectionMutations() {
  const collectFeesMutation = useMutationWithToast(trpc.collection.collectFees.useMutation, {
    successTitle: null,
    errorTitle: 'Collection failed'
  })

  const collectRecoveriesMutation = useMutationWithToast(trpc.collection.collectRecoveries.useMutation, {
    successTitle: null,
    errorTitle: 'Collection failed'
  })

  return {
    collectFees: collectFeesMutation.mutateAsync,
    collectRecoveries: collectRecoveriesMutation.mutateAsync,
    isCollecting: collectFeesMutation.isPending || collectRecoveriesMutation.isPending
  }
}

export function useRecoveryMutations() {
  const utils = trpc.useUtils()

  const invalidate = () => {
    void utils.collection.invalidate()
  }

  const rescheduleMutation = useMutationWithToast(
    trpc.collection.rescheduleRecovery.useMutation,
    { successTitle: 'Recovery rescheduled', errorTitle: 'Could not reschedule recovery' },
    { onSuccess: invalidate }
  )

  const cancelMutation = useMutationWithToast(
    trpc.collection.cancelRecovery.useMutation,
    { successTitle: 'Recovery canceled', errorTitle: 'Could not cancel recovery' },
    { onSuccess: invalidate }
  )

  return {
    rescheduleRecovery: rescheduleMutation.mutateAsync,
    cancelRecovery: cancelMutation.mutateAsync,
    isRescheduling: rescheduleMutation.isPending,
    isCanceling: cancelMutation.isPending
  }
}

export function useCollectionPaymentRetry() {
  const utils = trpc.useUtils()

  const retryMutation = useMutationWithToast(
    trpc.payment.retry.useMutation,
    { successTitle: 'Payment scheduled for retry', errorTitle: 'Could not retry payment' },
    {
      onSuccess: () => {
        void utils.collection.invalidate()
      }
    }
  )

  return {
    retryPayment: retryMutation.mutateAsync,
    isRetrying: retryMutation.isPending
  }
}

export function useAdvanceRecoveryPreview(traderId: number, offerId: number | null, enabled = true) {
  const { data, isLoading } = trpc.collection.advanceRecoveryPreview.useQuery(
    { traderId, offerId: offerId ?? 0 },
    { enabled: enabled && offerId != null && offerId > 0, staleTime: 0 }
  )
  return { preview: data ?? null, isLoading }
}

export function useAdvanceRecoveryMutations() {
  const utils = trpc.useUtils()

  const initiateMutation = useMutationWithToast(
    trpc.collection.initiateAdvanceRecovery.useMutation,
    {
      successTitle: 'Recovery payment scheduled for today',
      errorTitle: 'Could not schedule advance recovery'
    },
    {
      onSuccess: () => {
        void utils.collection.invalidate()
        void utils.invoiceOffer.list.invalidate()
      }
    }
  )

  return {
    initiateAdvanceRecovery: initiateMutation.mutateAsync,
    isInitiating: initiateMutation.isPending
  }
}
